import type { LogicFilterValue } from '../../fetchSurvey/types/survey';
import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { HeatmapQuestion } from '../../fetchSurvey/types/heatmap';
import type { RankOrderQuestion } from '../../fetchSurvey/types/rankOrder';
import type { LogicContext } from '../types';
import { LOGIC_FIELDS } from '../constants/logicFields';
import { getMatchedRegionLabels, normalizeHeatmapClicks } from './heatmapRegionUtils';
import { isMatrixAnswerMapEmpty, isMatrixQuestion, isMatrixRowAnswers } from './matrixQuestionUtils';
import { parseCompositeKey } from './parseCompositeKey';
import { isScalarMissing, resolveFieldValue } from './resolveFieldValue';
import { isRankOrderAnswerEmpty, normalizeRankOrderAnswers } from './rankOrderUtils';

function isMatrixRowValueEmpty(rowValue: unknown): boolean {
  if (rowValue === undefined || rowValue === null || rowValue === '') {
    return true;
  }
  if (Array.isArray(rowValue)) {
    return rowValue.length === 0;
  }
  return false;
}

function isMatrixCellValueEmpty(rowValue: unknown, columnId: string): boolean {
  if (rowValue === undefined || rowValue === null || rowValue === '') {
    return true;
  }

  if (Array.isArray(rowValue)) {
    return !rowValue.some(item => String(item) === columnId);
  }

  return String(rowValue) !== columnId;
}

function isMatrixRowCellEmpty(
  rowId: string,
  columnId: string | undefined,
  context: LogicContext,
  questionId: string
): boolean {
  const answer = context.answers[questionId];
  if (!isMatrixRowAnswers(answer)) {
    return true;
  }

  const rowValue = answer[rowId];

  if (columnId) {
    return isMatrixCellValueEmpty(rowValue, columnId);
  }

  return isMatrixRowValueEmpty(rowValue);
}

/**
 * Returns true when the answer is empty for MISSING / EXISTS checks.
 *
 * Matrix questions: empty when no row has a value, unless a composite key in
 * values targets a specific row/cell.
 */
export function isAnswerEmpty(
  field: string | undefined,
  questionId: string | undefined,
  context: LogicContext,
  values?: LogicFilterValue[]
): boolean {
  if (!field || !questionId) {
    return true;
  }

  const question = context.questionsById.get(questionId);

  if (field === LOGIC_FIELDS.NUM_OF_SELECTED_OPTIONS) {
    const count = resolveFieldValue(field, questionId, context);
    return typeof count !== 'number' || count === 0;
  }

  if (question && isMatrixQuestion(question)) {
    const compositeValue = values?.[0];
    if (
      compositeValue !== undefined &&
      (field === LOGIC_FIELDS.MATRIX_OPTIONS || field === LOGIC_FIELDS.SLIDER)
    ) {
      const parsed = parseCompositeKey(compositeValue);
      if (parsed) {
        return isMatrixRowCellEmpty(parsed.rowId, parsed.suffix, context, questionId);
      }
    }

    return isMatrixAnswerMapEmpty(context.answers[questionId]);
  }

  if (field === LOGIC_FIELDS.HEATMAP || field === LOGIC_FIELDS.HEATMAP_REGION) {
    if (!questionId) {
      return true;
    }

    const clicks = normalizeHeatmapClicks(context.answers[questionId]);

    if (field === LOGIC_FIELDS.HEATMAP) {
      return clicks.length === 0;
    }

    const targetLabel = values?.[0];
    if (targetLabel !== undefined && question?.type === QUESTION_TYPE.HEATMAP) {
      const heatmapQuestion = question as HeatmapQuestion;
      const matched = getMatchedRegionLabels(clicks, heatmapQuestion.regions);
      return !matched.includes(String(targetLabel));
    }

    return clicks.length === 0;
  }

  if (field === LOGIC_FIELDS.RANK_ORDER && question?.type === QUESTION_TYPE.RANK_ORDER) {
    const rankQuestion = question as RankOrderQuestion;
    const visibleOptionIds = rankQuestion.options.map(option => option.id);
    const rankAnswers = normalizeRankOrderAnswers(context.answers[questionId]);
    return isRankOrderAnswerEmpty(rankAnswers, visibleOptionIds, rankQuestion.requireRankAll);
  }

  return isScalarMissing(resolveFieldValue(field, questionId, context));
}
